export const MAX_INDICATOR_CHART_POINTS = 42;

const timestamp = (point, fallback) => {
    const value = Date.parse(point?.recorded_at || `${point?.date || ''}T12:00:00`);
    return Number.isFinite(value) ? value : fallback;
};

const uniqueCauses = (points) => {
    const seen = new Set();

    return points.flatMap(point => point.causes || []).filter(cause => {
        const key = String(cause?.id || cause?.label || '');
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

/**
 * Keeps every raw observation for statistics, but groups dense time series into
 * a readable mean curve. Each rendered point exposes its observation count and
 * time range for the tooltip/accessibility layer.
 */
export const buildIndicatorChartSeries = (history = [], maxPoints = MAX_INDICATOR_CHART_POINTS) => {
    const observations = [...history]
        .map((point, index) => ({...point, _timestamp: timestamp(point, index)}))
        .filter(point => Number.isFinite(Number(point.value)))
        .sort((left, right) => left._timestamp - right._timestamp);

    if (observations.length <= maxPoints) {
        return {points: observations.map(({_timestamp, ...point}) => ({...point, observation_count: 1})), rawCount: observations.length, aggregated: false};
    }

    const first = observations[0]._timestamp;
    const last = observations.at(-1)._timestamp;
    const span = Math.max(last - first + 1, observations.length);
    const bucketSize = span / maxPoints;
    const buckets = Array.from({length: maxPoints}, () => []);

    observations.forEach((point) => {
        const bucketIndex = Math.min(
            maxPoints - 1,
            Math.max(0, Math.floor((point._timestamp - first) / bucketSize)),
        );
        buckets[bucketIndex].push(point);
    });

    const points = buckets.filter(bucket => bucket.length).map(bucket => {
        const firstPoint = bucket[0];
        const lastPoint = bucket.at(-1);
        const average = bucket.reduce((sum, point) => sum + Number(point.value), 0) / bucket.length;
        const { _timestamp, ...latest } = lastPoint;

        return {
            ...latest,
            value: Math.round(average * 10000) / 10000,
            date: lastPoint.date,
            recorded_at: lastPoint.recorded_at,
            period_start: firstPoint.recorded_at || firstPoint.date,
            period_end: lastPoint.recorded_at || lastPoint.date,
            causes: uniqueCauses(bucket),
            observation_count: bucket.length,
        };
    });

    return {points, rawCount: observations.length, aggregated: true};
};

export const shouldRenderChartLabel = (index, count, maxLabels = 7) => {
    if (count <= maxLabels) return true;
    const interval = Math.ceil((count - 1) / (maxLabels - 1));
    return index === 0 || index === count - 1 || index % interval === 0;
};
